/**
 * Skill Detail page.
 *
 * Sprint 131 Stream C — tabs Overview / Instructions / Assets / Bindings /
 * Versions / Permissions / Activity. Versions, Permissions and Activity are
 * placeholders pending Sprints 132-134.
 */

import { useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import {
  Badge,
  Button,
  Card,
  Spinner,
  StatusBadge,
  Tabs,
  TabsList,
  TabTrigger,
  TabContent,
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
  TableEmpty,
  IconButton,
} from '@/components/ui';
import {
  ArrowLeft,
  Download,
  Edit3,
  FileText,
  Lock,
  Play,
  Plus,
  Shield,
  Sparkles,
  Trash2,
  Upload,
} from 'lucide-react';
import { MarkdownRenderer } from '@vanguard-atlas/shared/src/components/ui';
import {
  isWrappedImportedField,
  unwrapImportedField,
  wrapImportedField,
} from '@/lib/importedField';
import { useTenant } from '@/contexts/TenantContext';
import {
  useAdminSkill,
  useDeleteAdminSkillAsset,
  usePatchAdminSkill,
  useTransitionAdminSkill,
  useSkillEditors,
  useRemoveSkillEditor,
  useSkillGroupGrants,
  useInstallPlatformSkill,
} from '@/features/skills';
import type { SkillLifecycleStatus } from '@/features/skills';
import { SkillEditIdentityModal } from './modals/SkillEditIdentityModal';
import { AssetUploadModal } from './modals/AssetUploadModal';
import { SkillDeleteImpactModal } from './modals/SkillDeleteImpactModal';
import { SkillEditorAddModal } from './modals/SkillEditorAddModal';
import { SkillPlaygroundDrawer } from './modals/SkillPlaygroundDrawer';
import { SkillRestrictUseModal } from './modals/SkillRestrictUseModal';
import { SkillActivityTab } from './tabs/SkillActivityTab';
import { SkillVersionsTab } from './tabs/SkillVersionsTab';
import { getErrorMessage } from '@/lib/api-client';
import { userDisplayLabel } from '@/lib/user-label';
import { useI18n } from '@/i18n';

function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1024 / 1024).toFixed(2)} MB`;
}

function lifecycleStatusType(
  status: SkillLifecycleStatus,
): 'active' | 'draft' | 'warning' | 'error' | 'deprecated' | 'default' {
  switch (status) {
    case 'published':
      return 'active';
    case 'preview':
      return 'warning';
    case 'draft':
      return 'draft';
    case 'deprecated':
      return 'deprecated';
    case 'archived':
      return 'error';
    default:
      return 'default';
  }
}

const ALLOWED_TRANSITIONS: Record<
  SkillLifecycleStatus,
  SkillLifecycleStatus[]
> = {
  draft: ['preview', 'archived'],
  preview: ['published', 'draft', 'archived'],
  published: ['deprecated', 'archived'],
  deprecated: ['archived', 'published'],
  archived: [],
};

// ISS-259 — canonical lifecycle cycle. The UI only shows the next step
// (draft → preview → published → deprecated → archived, terminal), plus a
// discrete "Archive" action wherever archiving is allowed by the backend
// state machine (api/src/skills/service.py:_LIFECYCLE_TRANSITIONS).
const NEXT_LIFECYCLE_STEP: Partial<
  Record<SkillLifecycleStatus, SkillLifecycleStatus>
> = {
  draft: 'preview',
  preview: 'published',
  published: 'deprecated',
  deprecated: 'published',
};

export function SkillDetailPage() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const { currentTenant } = useTenant();
  const { t } = useI18n();

  // Resolve slug → skill via the list endpoint (cheaper than a /by-slug path
  // since the admin endpoint accepts both id and slug for /admin/skills/{slug}).
  // Backend supports GET /admin/skills/{slug} so we hit it directly; scope by
  // the current tenant so tenant-tier (BYOS) skills resolve (ISS-254).
  // ISS-289: wait for the tenant context before fetching — a platform-scoped
  // request 404s for tenant skills.
  const { data: skill, isLoading, error } = useAdminSkill(
    slug,
    currentTenant?.id,
    !!currentTenant,
  );

  const [editIdentityOpen, setEditIdentityOpen] = useState(false);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [playgroundOpen, setPlaygroundOpen] = useState(false);
  const [instructionsDraft, setInstructionsDraft] = useState<string | null>(
    null,
  );
  // ISS-564 (revisión general): recuerda si las instrucciones del skill venían
  // envueltas en `<imported_field>` para re-envolverlas al guardar (simetría).
  const importedInstructionsWrapperRef = useRef(false);

  const patch = usePatchAdminSkill(skill?.id ?? '');
  const transition = useTransitionAdminSkill(skill?.id ?? '');
  const deleteAsset = useDeleteAdminSkillAsset(skill?.id ?? '');
  const installSkill = useInstallPlatformSkill();

  const isPlatformTier =
    skill?.tier === 'platform_skill' ||
    skill?.tier === 'platform_official_skill';
  const isPublished = skill?.lifecycle_status === 'published';
  const canManageSkill = !isPlatformTier || currentTenant?.is_system_tenant === true;

  const nextStep = useMemo(
    () =>
      skill && canManageSkill ? NEXT_LIFECYCLE_STEP[skill.lifecycle_status] : undefined,
    [canManageSkill, skill],
  );
  const canArchive = useMemo(
    () =>
      !!skill &&
      canManageSkill &&
      ALLOWED_TRANSITIONS[skill.lifecycle_status].includes('archived'),
    [canManageSkill, skill],
  );

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Spinner size="lg" />
      </div>
    );
  }
  if (error || !skill) {
    return (
      <div className="p-6">
        <Card className="p-6 border-red-200 dark:border-red-800">
          <p className="text-red-600 dark:text-red-400">
            {t('admin.skills.detail.loadFailed', {
              slug: slug ?? '',
              message:
                (error as Error)?.message ?? t('admin.skills.detail.notFound'),
            })}
          </p>
          <Button
            variant="outline"
            className="mt-4"
            onClick={() => navigate('/skills')}
          >
            <ArrowLeft className="h-4 w-4 mr-2" />{' '}
            {t('admin.skills.detail.backToCatalog')}
          </Button>
        </Card>
      </div>
    );
  }

  async function handleTransition(target: SkillLifecycleStatus) {
    try {
      await transition.mutateAsync({ target });
      toast.success(t('admin.skills.detail.lifecycleChanged', { target }));
    } catch (err) {
      toast.error(getErrorMessage(err));
    }
  }

  async function handleInstall() {
    if (!skill) return;
    try {
      const installed = await installSkill.mutateAsync(skill.slug);
      toast.success(t('admin.skills.detail.installInTenantToast'));
      navigate(`/skills/${installed.slug}`);
    } catch (err) {
      toast.error(getErrorMessage(err));
    }
  }

  async function saveInstructions() {
    if (instructionsDraft === null) return;
    try {
      // ISS-564: re-envolver con `<imported_field>` si el skill importado lo
      // tenía (el editor muestra el texto desenvuelto).
      const payload =
        importedInstructionsWrapperRef.current &&
        !isWrappedImportedField(instructionsDraft)
          ? wrapImportedField(instructionsDraft)
          : instructionsDraft;
      await patch.mutateAsync({
        instructions: payload,
        instructions_format: 'markdown',
      });
      toast.success(t('admin.skills.detail.instructionsSaved'));
      setInstructionsDraft(null);
    } catch (err) {
      toast.error(getErrorMessage(err));
    }
  }

  return (
    <div className="p-6">
      {/* Header */}
      <div className="mb-6">
        <Link
          to="/skills"
          className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground mb-2"
        >
          <ArrowLeft className="h-4 w-4" /> {t('admin.skills.detail.skillsCatalog')}
        </Link>
        <div className="flex items-start justify-between gap-4">
          <div className="min-w-0">
            <div className="flex items-center gap-3 mb-1 flex-wrap">
              <Sparkles className="h-6 w-6 text-primary flex-shrink-0" />
              <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100 truncate">
                {skill.display_name}
              </h1>
              <Badge variant="outline" className="font-mono">
                {skill.slug}
              </Badge>
              <StatusBadge
                status={lifecycleStatusType(skill.lifecycle_status)}
                label={skill.lifecycle_status}
              />
              <Badge variant="secondary">{skill.tier.replace(/_/g, ' ')}</Badge>
              {skill.is_beta && (
                <Badge variant="secondary">{t('admin.skills.detail.beta')}</Badge>
              )}
            </div>
            {skill.description && (
              <p className="text-gray-600 dark:text-gray-400">
                {unwrapImportedField(skill.description)}
              </p>
            )}
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            {/* Install in tenant — only for published platform-tier skills */}
            {isPlatformTier && isPublished && (
              <Button
                size="sm"
                variant="outline"
                disabled={installSkill.isPending}
                onClick={handleInstall}
                title={t('admin.skills.detail.installInTenantTitle')}
              >
                <Download className="h-4 w-4 mr-2" />
                {t('admin.skills.detail.installInTenant')}
              </Button>
            )}
            {nextStep && (
              <Button
                size="sm"
                variant="outline"
                disabled={transition.isPending}
                onClick={() => handleTransition(nextStep)}
              >
                → {nextStep}
              </Button>
            )}
            {canArchive && (
              <Button
                size="sm"
                variant="ghost"
                disabled={transition.isPending}
                onClick={() => handleTransition('archived')}
              >
                {t('admin.skills.detail.archive')}
              </Button>
            )}
            <Button
              size="sm"
              variant="outline"
              onClick={() => setPlaygroundOpen(true)}
            >
              <Play className="h-4 w-4 mr-2" />
              {t('admin.skills.detail.playground')}
            </Button>
            {canManageSkill && (
              <>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setEditIdentityOpen(true)}
                >
                  <Edit3 className="h-4 w-4 mr-2" />
                  {t('admin.skills.detail.edit')}
                </Button>
                <Button
                  size="sm"
                  variant="destructive"
                  onClick={() => setDeleteOpen(true)}
                >
                  <Trash2 className="h-4 w-4 mr-2" />
                  {t('admin.skills.detail.delete')}
                </Button>
              </>
            )}
          </div>
        </div>
      </div>

      <Tabs defaultValue="overview">
        <TabsList className="mb-6">
          <TabTrigger value="overview">
            {t('admin.skills.detail.tabs.overview')}
          </TabTrigger>
          <TabTrigger value="instructions">
            {t('admin.skills.detail.tabs.instructions')}
          </TabTrigger>
          <TabTrigger value="assets">
            {t('admin.skills.detail.tabs.assets', { count: skill.assets.length })}
          </TabTrigger>
          <TabTrigger value="bindings">
            {t('admin.skills.detail.tabs.bindings')}
          </TabTrigger>
          <TabTrigger value="versions">
            {t('admin.skills.detail.tabs.versions')}
          </TabTrigger>
          {canManageSkill && (
            <TabTrigger value="permissions">
              {t('admin.skills.detail.tabs.permissions')}
            </TabTrigger>
          )}
          <TabTrigger value="activity">
            {t('admin.skills.detail.tabs.activity')}
          </TabTrigger>
        </TabsList>

        {/* OVERVIEW */}
        <TabContent value="overview">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Card className="p-4">
              <h3 className="font-semibold mb-3">
                {t('admin.skills.detail.classification')}
              </h3>
              <dl className="text-sm space-y-2">
                <div className="flex justify-between">
                  <dt className="text-muted-foreground">
                    {t('admin.skills.detail.vertical')}
                  </dt>
                  <dd>{skill.vertical}</dd>
                </div>
                <div className="flex justify-between">
                  <dt className="text-muted-foreground">
                    {t('admin.skills.detail.functionLabel')}
                  </dt>
                  <dd>{skill.function}</dd>
                </div>
                <div className="flex justify-between">
                  <dt className="text-muted-foreground">
                    {t('admin.skills.detail.category')}
                  </dt>
                  <dd>{skill.category}</dd>
                </div>
                <div className="flex justify-between">
                  <dt className="text-muted-foreground">
                    {t('admin.skills.detail.templateVersion')}
                  </dt>
                  <dd className="font-mono">{skill.template_version}</dd>
                </div>
                <div>
                  <dt className="text-muted-foreground mb-1">
                    {t('admin.skills.detail.tags')}
                  </dt>
                  <dd className="flex gap-1 flex-wrap">
                    {skill.tags.length === 0
                      ? '—'
                      : skill.tags.map((t) => (
                          <Badge key={t} variant="outline" className="text-xs">
                            {t}
                          </Badge>
                        ))}
                  </dd>
                </div>
              </dl>
            </Card>

            <Card className="p-4">
              <h3 className="font-semibold mb-3">
                {t('admin.skills.detail.activation')}
              </h3>
              <dl className="text-sm space-y-2">
                <div className="flex justify-between">
                  <dt className="text-muted-foreground">
                    {t('admin.skills.detail.defaultMode')}
                  </dt>
                  <dd>
                    <Badge variant="secondary">{skill.activation.mode}</Badge>
                  </dd>
                </div>
                <div>
                  <dt className="text-muted-foreground mb-1">
                    {t('admin.skills.detail.triggers')}
                  </dt>
                  <dd className="flex gap-1 flex-wrap">
                    {skill.activation.triggers.length === 0
                      ? '—'
                      : skill.activation.triggers.map((t) => (
                          <Badge key={t} variant="outline" className="text-xs">
                            {t}
                          </Badge>
                        ))}
                  </dd>
                </div>
              </dl>
            </Card>

            <Card className="p-4 md:col-span-2">
              <h3 className="font-semibold mb-3">
                {t('admin.skills.detail.dependencies')}
              </h3>
              <dl className="text-sm grid grid-cols-1 md:grid-cols-3 gap-3">
                <div>
                  <dt className="text-muted-foreground mb-1">
                    {t('admin.skills.detail.mcpServers')}
                  </dt>
                  <dd>
                    {(skill.requires.mcp_servers ?? []).length === 0
                      ? '—'
                      : skill.requires.mcp_servers!.join(', ')}
                  </dd>
                </div>
                <div>
                  <dt className="text-muted-foreground mb-1">
                    {t('admin.skills.detail.knowledgeCollections')}
                  </dt>
                  <dd>
                    {(skill.requires.knowledge_collections ?? []).length === 0
                      ? '—'
                      : skill.requires.knowledge_collections!.join(', ')}
                  </dd>
                </div>
                <div>
                  <dt className="text-muted-foreground mb-1">
                    {t('admin.skills.detail.otherSkills')}
                  </dt>
                  <dd>
                    {(skill.requires.skills ?? []).length === 0
                      ? '—'
                      : skill.requires.skills!.join(', ')}
                  </dd>
                </div>
              </dl>
            </Card>
          </div>
        </TabContent>

        {/* INSTRUCTIONS */}
        <TabContent value="instructions">
          <Card className="p-4">
            {instructionsDraft === null ? (
              <>
                <div className="flex items-center justify-between mb-3">
                  <h3 className="font-semibold">
                    {t('admin.skills.detail.instructionsTitle')}
                  </h3>
                  {canManageSkill && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => {
                        importedInstructionsWrapperRef.current =
                          isWrappedImportedField(skill.instructions);
                        setInstructionsDraft(unwrapImportedField(skill.instructions));
                      }}
                    >
                      <Edit3 className="h-4 w-4 mr-2" />{' '}
                      {t('admin.skills.detail.edit')}
                    </Button>
                  )}
                </div>
                <div className="prose prose-sm dark:prose-invert max-w-none">
                  <MarkdownRenderer content={unwrapImportedField(skill.instructions)} />
                </div>
              </>
            ) : (
              <>
                <div className="flex items-center justify-between mb-3">
                  <h3 className="font-semibold">
                    {t('admin.skills.detail.editMarkdownTitle')}
                  </h3>
                  <div className="flex items-center gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => setInstructionsDraft(null)}
                    >
                      {t('admin.skills.detail.cancel')}
                    </Button>
                    <Button
                      size="sm"
                      onClick={saveInstructions}
                      disabled={patch.isPending}
                    >
                      {patch.isPending
                        ? t('admin.skills.detail.saving')
                        : t('admin.skills.detail.save')}
                    </Button>
                  </div>
                </div>
                <textarea
                  className="w-full min-h-[400px] rounded-md border border-input bg-background p-3 text-sm font-mono"
                  value={instructionsDraft}
                  onChange={(e) => setInstructionsDraft(e.target.value)}
                />
              </>
            )}
          </Card>
        </TabContent>

        {/* ASSETS */}
        <TabContent value="assets">
          <Card className="p-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold">
                {t('admin.skills.detail.assetsTitle')}
              </h3>
              {canManageSkill && (
                <Button size="sm" onClick={() => setUploadOpen(true)}>
                  <Upload className="h-4 w-4 mr-2" />
                  {t('admin.skills.detail.uploadAsset')}
                </Button>
              )}
            </div>
            {skill.assets.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <FileText className="h-10 w-10 mx-auto mb-2 opacity-40" />
                {t('admin.skills.detail.noAssetsYet')}
              </div>
            ) : (
              <div className="space-y-2">
                {skill.assets.map((asset) => (
                  <div
                    key={asset.id}
                    className="flex items-center justify-between border rounded-md p-3"
                  >
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <FileText className="h-4 w-4 text-muted-foreground" />
                        <span className="font-mono text-sm">{asset.asset_id}</span>
                        <Badge variant="outline" className="text-xs">
                          {asset.type}
                        </Badge>
                        <Badge variant="secondary" className="text-xs">
                          {asset.inject}
                        </Badge>
                      </div>
                      <div className="text-xs text-muted-foreground mt-1">
                        {formatBytes(asset.size_bytes)}
                        {asset.mime_type ? ` · ${asset.mime_type}` : ''}
                      </div>
                    </div>
                    {canManageSkill && (
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={async () => {
                          if (
                            !confirm(
                              t('admin.skills.detail.confirmDeleteAsset', {
                                assetId: asset.asset_id,
                              }),
                            )
                          )
                            return;
                          try {
                            await deleteAsset.mutateAsync(asset.id);
                            toast.success(
                              t('admin.skills.detail.assetDeleted'),
                            );
                          } catch (err) {
                            toast.error(getErrorMessage(err));
                          }
                        }}
                      >
                        <Trash2 className="h-4 w-4 text-destructive" />
                      </Button>
                    )}
                  </div>
                ))}
              </div>
            )}
          </Card>
        </TabContent>

        {/* BINDINGS (placeholder — populated when listing agents that use this skill) */}
        <TabContent value="bindings">
          <Card className="p-6 text-center text-muted-foreground">
            {t('admin.skills.detail.bindingsInfo1')}
            <strong>{t('admin.skills.detail.bindingsInfo2')}</strong>
            {t('admin.skills.detail.bindingsInfo3')}
            <div className="mt-4">
              <Button asChild variant="outline" size="sm">
                <Link to="/agents">{t('admin.skills.detail.viewAgents')}</Link>
              </Button>
            </div>
          </Card>
        </TabContent>

        <TabContent value="versions">
          <SkillVersionsTab
            skillId={skill.id}
            lifecycleStatus={skill.lifecycle_status}
          />
        </TabContent>
        {canManageSkill && (
          <TabContent value="permissions">
            <PermissionsTab skillId={skill.id} />
          </TabContent>
        )}
        <TabContent value="activity">
          <SkillActivityTab skillId={skill.id} />
        </TabContent>
      </Tabs>

      {canManageSkill && (
        <>
          <SkillEditIdentityModal
            open={editIdentityOpen}
            onOpenChange={setEditIdentityOpen}
            skill={skill}
          />
          <AssetUploadModal
            open={uploadOpen}
            onOpenChange={setUploadOpen}
            skillId={skill.id}
          />
          <SkillDeleteImpactModal
            open={deleteOpen}
            onOpenChange={setDeleteOpen}
            skillId={skill.id}
            slug={skill.slug}
            collections={skill.asset_collections}
            onDeleted={() => navigate('/skills')}
          />
        </>
      )}
      <SkillPlaygroundDrawer
        open={playgroundOpen}
        onOpenChange={setPlaygroundOpen}
        skill={skill}
      />
    </div>
  );
}

export default SkillDetailPage;

// =============================================================================
// Permissions tab — M-A12 (editors) + M-A13 (group restrictions, Sprint 133 Stream C)
// =============================================================================
function PermissionsTab({ skillId }: { skillId: string }) {
  const { data, isLoading } = useSkillEditors(skillId);
  const removeEditor = useRemoveSkillEditor(skillId);
  const grantsQuery = useSkillGroupGrants(skillId);
  const [addOpen, setAddOpen] = useState(false);
  const [restrictOpen, setRestrictOpen] = useState(false);
  const { t } = useI18n();

  const editors = data?.editors ?? [];
  const editorIds = editors.map((e) => e.user_id);
  const grants = grantsQuery.data?.grants ?? [];
  const isRestricted = grants.length > 0;

  async function handleRemove(userId: string) {
    if (!confirm(t('admin.skills.detail.permissions.revokeConfirm', { userId }))) {
      return;
    }
    try {
      await removeEditor.mutateAsync(userId);
      toast.success(t('admin.skills.detail.permissions.removedEditor', { userId }));
    } catch (err) {
      toast.error(getErrorMessage(err));
    }
  }

  return (
    <div className="space-y-4">
      <Card className="p-6 space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-semibold flex items-center gap-2">
              <Shield className="h-5 w-5 text-primary" />
              {t('admin.skills.detail.permissions.editorsTitle')}
            </h3>
            <p className="text-sm text-muted-foreground">
              {t('admin.skills.detail.permissions.editorsDescription')}
            </p>
          </div>
          <Button onClick={() => setAddOpen(true)}>
            <Plus className="h-4 w-4 mr-2" />
            {t('admin.skills.detail.permissions.addEditor')}
          </Button>
        </div>

        {isLoading ? (
          <div className="flex justify-center py-6">
            <Spinner />
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t('admin.skills.detail.permissions.userIdColumn')}</TableHead>
                <TableHead className="w-12" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {editors.length === 0 ? (
                <TableEmpty
                  colSpan={2}
                  message={t('admin.skills.detail.permissions.noEditorGrants')}
                />
              ) : (
                editors.map((e) => (
                  <TableRow key={e.user_id}>
                    <TableCell className="text-sm">
                      {userDisplayLabel(e.email, e.name, e.user_id, t('admin.skills.detail.permissions.emailUnavailable'))}
                      {e.email && e.name && e.name !== e.email ? (
                        <span className="block text-xs text-muted-foreground">
                          {e.name}
                        </span>
                      ) : null}
                    </TableCell>
                    <TableCell>
                      <IconButton
                        icon="trash"
                        label={t('admin.skills.detail.permissions.revokeEditor')}
                        onClick={() => handleRemove(e.user_id)}
                      />
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        )}

        <SkillEditorAddModal
          open={addOpen}
          onOpenChange={setAddOpen}
          skillId={skillId}
          existingUserIds={editorIds}
        />
      </Card>

      {/* M-A13 — group restrictions */}
      <Card className="p-6 space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-semibold flex items-center gap-2">
              <Lock className="h-5 w-5 text-primary" />
              {t('admin.skills.detail.permissions.useRestrictionsTitle')}
            </h3>
            <p className="text-sm text-muted-foreground">
              {t('admin.skills.detail.permissions.restrictionsDescription')}
            </p>
          </div>
          <Button variant="outline" onClick={() => setRestrictOpen(true)}>
            {t('admin.skills.detail.permissions.manageGroups')}
          </Button>
        </div>

        {grantsQuery.isLoading ? (
          <div className="flex justify-center py-6">
            <Spinner />
          </div>
        ) : !isRestricted ? (
          <p className="text-sm text-muted-foreground">
            {t('admin.skills.detail.permissions.openToAll')}
          </p>
        ) : (
          <ul className="space-y-1">
            {grants.map((g) => (
              <li
                key={`${g.relation}:${g.group_id}`}
                className="flex items-center gap-2 text-sm"
              >
                <span className="rounded border px-2 py-0.5 text-xs">
                  {g.relation === 'editors'
                    ? t('admin.skills.restrictUse.badgeEdit')
                    : t('admin.skills.restrictUse.badgeUse')}
                </span>
                <span className="font-mono text-xs">{g.group_id}</span>
              </li>
            ))}
          </ul>
        )}

        <SkillRestrictUseModal
          open={restrictOpen}
          onOpenChange={setRestrictOpen}
          skillId={skillId}
        />
      </Card>
    </div>
  );
}
